import React, { useState, useMemo, useCallback, ReactElement } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog, DialogTitle, Box, IconButton, Typography, CircularProgress } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { J2Multiply } from '@e3dc-react/icons'
import { InstallationStatement } from '../InstallationStatement'
import { IncompleteStatementDialog } from 'src/Libs/installation-statement/components/Modals/index'
import { useInstallationStatement } from 'src/Libs/installation-statement'
import { StatementFormData } from 'src/Libs/installation-statement/types/general'
import { useFetchData } from 'src/Libs/installation-statement/hooks'
import { fromStatementModelToDtoTransformer } from 'src/Libs/installation-statement/transformers/from-statement-model-to-dto.transformer'
import { DEFAULT_PHOTOVOLTAIC_DATA } from 'src/Libs/installation-statement/configurations/photovoltaic'
import useMeasurementsValidation from 'src/Libs/installation-statement/hooks/validation/use-measurements-validation'
import { useAddressValidation, usePhotovoltaicValidation, useStorageValidation } from 'src/Libs/installation-statement/hooks/validation'
import useImageValidation from 'src/Libs/installation-statement/hooks/validation/use-image-validation'
import useEdisonValidation from 'src/Libs/installation-statement/hooks/validation/use-edison-validation'
import LoadingError from 'src/Libs/installation-statement/components/Modals/ModalState/LoadingError'
import FinalSaveError from 'src/Libs/installation-statement/components/Modals/ModalState/FinalSaveError'
import InterimSaveError from 'src/Libs/installation-statement/components/Modals/ModalState/InterimSaveError'
import SaveGenerating from './ModalState/SaveGenerating'
import SaveSuccess from 'src/Libs/installation-statement/components/Modals/ModalState/SaveSuccess'
import { useDispatch } from 'react-redux'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { DEFAULT_STORAGE_DATA } from '../../configurations/storage'
import { DEFAULT_MEASUREMENTS_DATA } from '../../configurations/measurements'
import { DEFAULT_EDISON_WALLBOX_DATA } from '../../configurations/edisonWallBox'
import { AddressFormData } from '../../types'
import { isEqual } from 'lodash'
import { ImagesFormData } from '../../models/images'

const useStyles = makeStyles()(theme => ({
  dialogHeader: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.shades.shade04,
    borderRadius: `${theme.shape.borderRadius}px  ${theme.shape.borderRadius}px 0 0`,
    padding: `0 ${theme.space(4)}px`,
    display: 'flex',
    height: '66px',
    alignItems: 'center',
    margin: '0',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      borderRadius: '0',
    },
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  dialogIconButton: {
    flexDirection: 'column',
    color: theme.palette.common.white,
    width: '28px',
    height: '28px',
  },
  dialogRoot: {
    position: 'relative',
    maxWidth: '100%',
    minWidth: '50%',
    width: '1000px',
    minHeight: '50%',
    height: '90%',
    padding: 0,
    margin: 0,
    //to hide scrollbar in FF
    scrollbarWidth: 'none',
    backgroundColor: 'transparent',
    borderRadius: theme.shape.borderRadius,
    //to hide scrollbar in Chrome,Edge and safari
    '&::-webkit-scrollbar': {
      display: 'none',
    },

    [theme.breakpoints.down('sm')]: {
      borderRadius: '0',
      width: '100%',
      height: '100%',
      minHeight: '100%',
      maxWidth: '100%',
    },
  },

  loader: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },

  dialogBackground: {
    backgroundColor: theme.palette.grey.grey07,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
    height: 'calc(100% - 66px)',
    paddingBottom: 60,
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
  },
}))

export type DeviceInfoForStatement = {
  id: number
  installationStatementId?: number
  deviceLinkId: number
  deviceId: string
  installer?: string
  installerCompanyId?: number
}

export interface InstallationStatementDialogProps {
  place: number
  onClose: () => void
  installation: DeviceInfoForStatement
}

/**
 * This component provide a modal dialog to display installation statements wizard in the company dashboard.
 * @param place provide the index number in the modal list for redux
 * @param onClose  provide closing function by removing it from redux list
 * @returns  a Dialog with custom content which is the wizard in this case
 */
const InstallationStatementDialog: React.FC<InstallationStatementDialogProps> = ({ onClose, installation }) => {
  const dispatch = useDispatch()
  const { t } = useCustomTranslation()
  const { finalValidationSchema: finalMeasurementsValidation } = useMeasurementsValidation()
  const { finalValidationSchema: finalAddressValidation } = useAddressValidation()
  const { finalValidationSchema: finalStorageValidation } = useStorageValidation()
  const { finalValidationSchema: finalPvValidation } = usePhotovoltaicValidation()
  const { finalValidationSchema: finalImagesValidation } = useImageValidation()
  const { finalValidationSchema: finalEdisonValidation } = useEdisonValidation()
  const [error, setError] = useState<false | 'loading' | 'save' | 'finalSave'>(false)
  const [statementSubmitted, setStatementSubmitted] = useState<false | 'inProgress' | true>(false)

  const { addressStep, storageStep, photovoltaicStep, overviewStep, imagesStep, measurementsStep, edisonStep } = useInstallationStatement(
    installation.deviceLinkId,
    installation.id,
    installation.installer
  )
  const { classes } = useStyles()
  const [incompleteShown, setIncompleteShown] = useState<boolean>(false)

  const isAddressFilled = (addressData: AddressFormData): boolean => {
    return (
      addressData.customerAddress.addressAndHouseNumber !== '' ||
      addressData.customerAddress.city !== '' ||
      addressData.customerAddress.country !== '' ||
      addressData.customerAddress.firstName !== '' ||
      addressData.customerAddress.lastName !== '' ||
      addressData.customerAddress.zipCode !== ''
    )
  }

  const areImagesFilled = (imagesData: ImagesFormData): boolean => {
    return (
      imagesData.images.complete.id !== undefined || imagesData.images.batteries.id !== undefined || imagesData.images.connection.id !== undefined
    )
  }

  const setValues = useCallback(
    (params: { statementFormData: StatementFormData; hasError: false } | { hasError: true }): void => {
      if (params.hasError) {
        setError('loading')
        return
      }

      const { statementFormData } = params

      addressStep.setFormData({
        values: {
          ...statementFormData.addressData,
          installer: {
            firstName: installation.installer?.split(' ')[0] ?? '',
            lastName: installation.installer?.split(' ').slice(1).join(' ') ?? '',
          },
        },
      })
      addressStep.setFinalValidation(finalAddressValidation.isValidSync(statementFormData.addressData))
      addressStep.setStepIsPrefilled(isAddressFilled(statementFormData.addressData))

      storageStep.setFormData({ values: statementFormData.storageData })
      storageStep.setFinalValidation(finalStorageValidation.isValidSync(statementFormData.storageData))
      storageStep.setStepIsPrefilled(!isEqual(DEFAULT_STORAGE_DATA, statementFormData.storageData))

      const pvValues = statementFormData.photovoltaicData.pvTrackers.length === 0 ? DEFAULT_PHOTOVOLTAIC_DATA : statementFormData.photovoltaicData
      photovoltaicStep.setFormData({ values: pvValues })
      photovoltaicStep.setFinalValidation(finalPvValidation.isValidSync(pvValues))
      photovoltaicStep.setStepIsPrefilled(statementFormData.photovoltaicData.pvTrackers.length !== 0)

      imagesStep.setFormData({ values: statementFormData.imagesData })
      imagesStep.setFinalValidation(finalImagesValidation.isValidSync(statementFormData.imagesData))
      imagesStep.setStepIsPrefilled(areImagesFilled(statementFormData.imagesData))

      measurementsStep.setFormData({ values: statementFormData.measurementsData })
      measurementsStep.setFinalValidation(finalMeasurementsValidation.isValidSync(statementFormData.measurementsData))
      measurementsStep.setStepIsPrefilled(!isEqual(DEFAULT_MEASUREMENTS_DATA, statementFormData.measurementsData))

      edisonStep.setFormData({ values: statementFormData.edisonData })
      edisonStep.setFinalValidation(finalEdisonValidation.isValidSync(statementFormData.edisonData))
      edisonStep.setStepIsPrefilled(!isEqual(DEFAULT_EDISON_WALLBOX_DATA, statementFormData.edisonData))
    },
    [
      addressStep,
      storageStep,
      edisonStep,
      photovoltaicStep,
      imagesStep,
      measurementsStep,
      finalMeasurementsValidation,
      finalAddressValidation,
      finalStorageValidation,
      finalPvValidation,
      finalImagesValidation,
      finalEdisonValidation,
      installation.installer,
    ]
  )

  const defaultValues: StatementFormData = {
    addressData: addressStep.formData.values,
    photovoltaicData: photovoltaicStep.formData.values,
    imagesData: imagesStep.formData.values,
    storageData: storageStep.formData.values,
    measurementsData: measurementsStep.formData.values,
    edisonData: edisonStep.formData.values,
  }

  const { isLoading, saveStatement, confirmStatement } = useFetchData(installation, defaultValues, setValues)

  /**
   * Checking if any of the forms has unsaved changes
   * @returns boolean
   */
  const hasUnsavedChanges = useMemo((): boolean => {
    return [addressStep, storageStep, edisonStep, photovoltaicStep, imagesStep, measurementsStep, overviewStep].some(step => {
      return step.isTouched && step.formData.dirty
    })
  }, [addressStep, storageStep, edisonStep, photovoltaicStep, imagesStep, measurementsStep, overviewStep])

  /**
   * Checking if none of the steps contain invalid data
   * @returns boolean
   */
  const areAllStepsValid = useMemo((): boolean => {
    return [addressStep, storageStep, photovoltaicStep, imagesStep, measurementsStep, edisonStep].every(step => {
      return step.formData.isValid || !step.formData.dirty
    })
  }, [addressStep, storageStep, photovoltaicStep, measurementsStep, imagesStep, edisonStep])

  /**
   * Preparing data for the api call
   */
  const getTransformedData = useCallback(() => {
    const result = {
      addressData: { ...addressStep.formData.values },
      photovoltaicData: { ...photovoltaicStep.formData.values },
      storageData: { ...storageStep.formData.values },
      measurementsData: { ...measurementsStep.formData.values },
      imagesData: { ...imagesStep.formData.values },
      edisonData: { ...edisonStep.formData.values },
    }

    return fromStatementModelToDtoTransformer(result)
  }, [
    addressStep.formData.values,
    storageStep.formData.values,
    edisonStep.formData.values,
    photovoltaicStep.formData.values,
    measurementsStep.formData.values,
    imagesStep.formData.values,
  ])

  /**
   * Saving incomplete statement
   */
  const onSave = useCallback(async (): Promise<void> => {
    const data = getTransformedData()
    const response = await saveStatement(installation.deviceLinkId, installation.id, data)

    if (!response) {
      setError('save')
      return
    }

    dispatch(refreshApiView())
    closeDialog()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTransformedData])

  /**
   * Saving and confirming a completed statement
   */
  const onConfirm = useCallback(async (): Promise<void> => {
    setStatementSubmitted('inProgress')
    const data = getTransformedData()
    const response = await confirmStatement(installation.deviceLinkId, installation.id, data)

    if (response.hasErrors) {
      setError('finalSave')
      return
    } else {
      dispatch(refreshApiView())
      setStatementSubmitted(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTransformedData])

  const closeWithCheck = (): void => {
    if (hasUnsavedChanges) {
      setIncompleteShown(true)
    } else {
      closeDialog()
    }
  }

  const closeDialog = (): void => {
    setIncompleteShown(false)
    if (onClose) {
      onClose()
    }
  }

  const wizardHeader = (
    <DialogTitle className={classes.dialogHeader} component={'div'}>
      <Typography variant={'h3'}>
        {installation?.id} {t('installation-statement.dialog.title')} {installation.deviceId}
      </Typography>
      <IconButton className={classes.dialogIconButton} onClick={closeWithCheck}>
        <J2Multiply />
      </IconButton>
    </DialogTitle>
  )

  const incompleteWarning = incompleteShown && (
    <IncompleteStatementDialog onClose={() => setIncompleteShown(false)} onLeave={closeDialog} onSave={onSave} isSaveEnabled={areAllStepsValid} />
  )

  const renderErrorScreens = (error: 'loading' | 'save' | 'finalSave'): ReactElement => {
    if (error === 'save') {
      return <InterimSaveError onClose={onClose} onBack={() => setError(false)} />
    } else if (error === 'finalSave') {
      return (
        <FinalSaveError
          onClose={onClose}
          onBack={() => {
            setError(false)
            setStatementSubmitted(false)
          }}
        />
      )
    } else {
      return <LoadingError onClose={onClose} />
    }
  }

  const renderSubmitScreen = (): ReactElement => {
    if (statementSubmitted === 'inProgress') {
      return <SaveGenerating />
    }

    return <SaveSuccess onClose={onClose} installationId={installation.id} deviceId={installation.deviceId} />
  }

  return (
    <>
      {incompleteWarning}
      <Dialog classes={{ paper: classes.dialogRoot }} open={true}>
        {wizardHeader}
        <Box className={classes.dialogBackground}>
          {error ? (
            renderErrorScreens(error)
          ) : statementSubmitted ? (
            renderSubmitScreen()
          ) : isLoading ? (
            <Box className={classes.loader}>
              <CircularProgress size={56} />
            </Box>
          ) : (
            <InstallationStatement
              addressStep={addressStep}
              storageStep={storageStep}
              overviewStep={overviewStep}
              photovoltaicStep={photovoltaicStep}
              imagesStep={imagesStep}
              measurementsStep={measurementsStep}
              onConfirm={onConfirm}
              edisonStep={edisonStep}
            />
          )}
        </Box>
      </Dialog>
    </>
  )
}
export default InstallationStatementDialog
